import { useEffect, useState } from 'react';
import { useGetERApplication } from './useERQueries';
import UnifyThemeProvider from '../v2/theme/UnifyThemeProvider';
import ERHeader from './ERHeader';
import {
  AppFrame,
  StyledOutageNotificationContainer,
  StyledOutageNotificationContainerSpacer,
  StyledWorkflowContainer,
  IconStatusInformationIcon,
  StyledOutageNotificationText,
  IconStatusInformationTrendUpIcon,
  StyledAlertTitle,
} from './styles';
import { Security } from '@okta/okta-react';
import { toRelativeUrl, OktaAuth } from '@okta/okta-auth-js';
import { oktaAuth } from '../v2/okta/authService';
import { useFeatureFlag } from '@harnessio/ff-react-client-sdk';
import { FFContextProvider } from '@harnessio/ff-react-client-sdk';
import { LOGOUT_URL, REACT_APP_HARNESS } from '../constants';
import { Redirect } from 'react-router-dom';
import { Suspense } from 'react';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import { AppLoader } from '../v2/common/AppLoader';
import { SecureRoute, LoginCallback } from '@okta/okta-react';
import { getDMSProxyFirewallUrl } from '../util/getDMSProxyUrls';
import { useOktaAuth } from '@okta/okta-react';
import { driveLaunchFallback } from '../util/driveLaunchFallback';
import { driveLaunch } from '../util/driveLaunch';
import get from 'lodash/get';
import { clearLocalStorageExceptDMS } from '../util/clearLocalStorage';
import { getDefaultApp } from './defaultApp';
import { useOktaUserProfileData } from '../v2/core/useUserProfileQueries';
import { useUnifyER } from '../AppState';
import { PrimaryButton } from 'cdk-radial';
import {Tabs} from './UnifyERTabs';

export function LoginCallbackComponent(props: any) {
  return (
    <LoginCallback
      {...props}
      loadingElement={<AppLoader loaderText="CDK Unify" />}
    />
  );
}

function Container({
  children,
  isHome,
  closedHeight,
}: {
  children: React.ReactNode;
  isHome: boolean;
  closedHeight?: string;
}) {
  return (
    <StyledWorkflowContainer
      data-testid="workflow-container"
      isHome={isHome}
      height={closedHeight}
    >
      {children}
    </StyledWorkflowContainer>
  );
}

export async function restoreOriginalUri(
  _oktaAuth: OktaAuth,
  originalUri?: string
): Promise<void> {
  const url = toRelativeUrl(originalUri || '/unify', window.location.origin);
  window.location.replace(url);
}

const harnessTargeIdentifiers = (authState: any) => {
  const enterpriseId = authState?.accessToken?.claims?.enterpriseId || '';
  let email = authState?.accessToken?.claims?.cdksid || '';
  email = email.replace(/[^a-zA-Z0-9@.]/g, '');

  return {
    identifier: email,
    name: email,
    attributes: {
      enterpriseId: enterpriseId,
      email: email,
    },
  };
};

function UnifyERApp() {
  return (
    <UnifyThemeProvider>
      <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
        <UnifySecureERApp></UnifySecureERApp>
      </Security>
    </UnifyThemeProvider>
  );
}

function UnifySecureERApp() {
  const { authState } = useOktaAuth();
  return (
    <>
      {(
        <FFContextProvider
          async={true}
          apiKey={REACT_APP_HARNESS}
          target={harnessTargeIdentifiers(authState)}
          options={{
            streamEnabled: true,
          }}
        >
          <Router>
            <Suspense
              fallback={<AppLoader loaderText="CDK Unify ER" />}
            ></Suspense>
            <SecureRoute
              exact
              path="/"
              render={() => <Redirect to={'/er-unify'} />}
            />
            <Route path="/login/callback" component={LoginCallbackComponent} />
            <SecureRoute path="/er-unify" component={UnifyERAppDetails} />
          </Router>
        </FFContextProvider>
      )}
    </>
  );
}

export function canOpenDrive() {
  return navigator.userAgent.toLowerCase().indexOf('win') !== -1;
}

export function launchInNewTab() {
  return ['cdk billing center', 'myinstall', 'customer care'];
}

function UnifyERAppDetails() {
  const { data, isLoading } = useGetERApplication();
  const defaultApps = getDefaultApp();
  const customProtocolFlag = useFeatureFlag('UNIFY_UI_DRIVE_CP_FLAG');
  const enableIdpProxyFlag = useFeatureFlag('UNIFY_ENABLE_IDP_PROXY');
  const [height, setHeight] = useState('130px');
  const { oktaAuth } = useOktaAuth();
  const { oktaProfileData } = useOktaUserProfileData(enableIdpProxyFlag);
  const [logout, setLogout] = useState("");

  const [sortedApplications, setSortedApplications] = useState([] as any);
  const [isHome, setIsHome] = useState(true);
  const [activeApp, setActiveApp] = useState({ name: '', id: '' });

  const UNIFY_UI_ER = useFeatureFlag('UNIFY_UI_ER');
  const [systemLive, setSystemLive] = useState(false);

  const session = useOktaAuth();
  const authToken = get(session, 'authState.idToken.idToken', '');

  const { setUnifyER } = useUnifyER();

  const onProceed = async () => {
    let driveLaunchResponse;
    let cNumber = data?.cnumbers?.[0];
    const { url, cNumber: dmsCNumber, driveToken } = getDMSProxyFirewallUrl({
      cNumber: cNumber,
      driveToken: authToken,
      lang: 'en_US',
    });

    if (customProtocolFlag) {
      driveLaunchResponse = await driveLaunch(url, dmsCNumber, driveToken);
    } else {
      driveLaunchResponse = await driveLaunchFallback(url);
    }
    console.log(driveLaunchResponse, ':drive launch');
  };

  useEffect(() => {
    if (UNIFY_UI_ER === false) {
      setSystemLive(true);
      setHeight('110px');
    }
  }, [UNIFY_UI_ER]);

  useEffect(() => {
    // Only merge when both queries have completed loading
    let sorted = data?.applications ? [...data.applications] : [];
    let cNumber = data?.cnumbers?.[0];
    sorted = [...defaultApps, ...sorted];
    sorted = sorted.filter(
      (app: any) => app.productName !== 'Unify Administration'
    );

    // Add driveApp if driveData exists
    if (cNumber) {
      const driveApp = {
        id: 'drive',
        productName: 'DRIVE',
        unifyAppUrl: '',
        workflows: [],
      };

      const appExists = sorted.some((app) => app.id === 'drive');
      if (!appExists) {
        sorted.push(driveApp);
      }
    }

    // Sort the array
    if (sorted.length > 0) {
      sorted = sorted.sort((a, b) =>
         (a?.productName ?? '').localeCompare(b?.productName ?? '')
      );
      setSortedApplications(sorted);
    } else {
      setSortedApplications([]);
    }
    // eslint-disable-next-line  react-hooks/exhaustive-deps
  }, [ data?.applications]);

  const launchUrl = (app: any) => {

    console.log("launch ",app);

    if (canOpenDrive() === false && app.id === 'drive') {
      return;
    } else if (app.id === 'drive') {
      console.log('Launching app: ', app);
      onProceed();
      return;
    }

    const newtabApps = launchInNewTab();
    if (newtabApps.includes(app.name?.toLowerCase())) {
      window.open(app.url, '_blank');
      return;
    }

    const iFrame = document.getElementById(
      'unify-er-frame-main'
    ) as HTMLIFrameElement;

    setActiveApp(app);
    iFrame.contentWindow!.location.href = 'about:blank';
    setTimeout(() => (iFrame.contentWindow!.location.href = app.url), 0);

    setIsHome(false);
  };

  const onExpandCollopse = () => {
    if (activeApp?.name === '' && isHome) {
      setIsHome(true);
    } else {
      setIsHome(!isHome);
    }
  };


  const handleLogout = async () => {
    try {
      // Clear auth and storage
      const idToken = oktaAuth.getIdToken();
      await oktaAuth.tokenManager.clear();
      clearLocalStorageExceptDMS();
      sessionStorage.clear();

      // Perform Okta logout
      if (idToken) {
        await oktaAuth.signOut({
          postLogoutRedirectUri: `${LOGOUT_URL}?source=unify&id_token_hint=${encodeURIComponent(String(idToken))}`
        });
      } else {
        window.location.assign(`${LOGOUT_URL}?source=unify`);
      }
    } catch (error) {
       console.error('Error during logout:', error);
    }
}

  useEffect(() => {
    const logoutHandler = async () => {
      if (logout === "DONE") {
        await handleLogout();
      }
    };
    logoutHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout]);


  return (
    <>

        {(logout === "INPROGRESS" || logout === "DONE") && <AppLoader loaderText="Logout in progress" />}

        <>

        { (logout !== "INPROGRESS" && logout !== "DONE") &&
        <div style={{ width: '100%' }}>
        <ERHeader
          oktaProfileData={oktaProfileData}
          isHome={isHome}
          toggleView={() => onExpandCollopse()}
          setLogout={setLogout}
          handleLogout={handleLogout}
        />
      </div>
      }



      {!isLoading && (logout !== "INPROGRESS" && logout !== "DONE") &&(
        <StyledOutageNotificationContainerSpacer>
          <StyledOutageNotificationContainer>
            {!systemLive ? (
              <div
                style={{
                  height: '80px',
                  marginLeft: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                    }}
                  >
                    <IconStatusInformationIcon />
                  </div>
                  <StyledAlertTitle>
                    Unify is experiencing some service disruption.
                  </StyledAlertTitle>
                </div>
                <StyledOutageNotificationText>
                  <span title="We are working to restore all services as soon as possible. You’re currently in limited mode, where you can continue working with available features.">
                    We are working to restore all services as soon as possible.
                    You’re currently in limited mode, where you can continue working with available features.
                  </span>
                </StyledOutageNotificationText>
              </div>
            ) : (
              <div
                style={{
                  height: '60px',
                  marginLeft: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingRight: '10px',
                    marginTop: '5px',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                      <div>
                        <IconStatusInformationTrendUpIcon />
                      </div>
                      <StyledAlertTitle>
                        All Unify services are restored.
                      </StyledAlertTitle>
                    </div>
                    <div
                      title="Return to the full experience by relaunching Unify."
                      style={{ marginLeft: '30px', marginTop: '-5px' }}
                    >
                      <span>
                        Return to the full experience by relaunching Unify.
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      fontWeight: 'bold',
                      color: 'blue',
                      marginLeft: '50px',
                    }}
                  >
                    <PrimaryButton
                      title={'Launch Unify'}
                      style={{
                        border: 'none',
                        fontFamily: 'Roboto, Montserrat, sans-serif',
                        fontWeight: 600,
                      }}
                      onClick={() => setUnifyER('false')}
                      text={'Launch Unify'}
                    />
                  </div>
                </div>
              </div>
            )}
          </StyledOutageNotificationContainer>
        </StyledOutageNotificationContainerSpacer>
      )}

      <div
        style={{
          marginTop: '0px',
          marginLeft: '2px',
          marginRight: '2px',
          width: '99vw',
          height: `calc(100% - ${height})`,
        }}
      >
        {!isLoading && (logout !== "INPROGRESS" && logout !== "DONE") &&(
          <Container isHome={isHome}>
            <div style={{ marginTop: '20px' }}>
              <Tabs
                sortedApplications={sortedApplications}
                launchUrl={launchUrl}
              ></Tabs>
            </div>
           
          </Container>
        )}

        {isLoading ? (
          <AppLoader loaderText="Unify is experiencing degradation, preparing your applications and workflows. Hang tight!" />
        ) : null}


        <AppFrame style={{ height: isHome ? '0px' : '100%' }}>
          <iframe
            title="Unify ER Application"
            id="unify-er-frame-main"
            data-testid="workflow-container"
            allow="camera; microphone; clipboard-read; clipboard-write; allow-same-origin; local-network-access *;"
            style={{
              width: '100%',
              height: '100%',
              margin: 0,
              padding: 0,
              border: 'none',
            }}
          ></iframe>
        </AppFrame>


      </div> </>
    </>
  );
}

export default UnifyERApp;
